import { formatDate } from '@angular/common';
import { inject, Injectable, Pipe, PipeTransform } from '@angular/core';

import { TranslationService } from '../services/translation.service';

@Injectable({
    providedIn: 'root'
})
@Pipe({
    name: 'localizedDate',
    standalone: false
})
export class LocalizedDatePipe implements PipeTransform {
    private readonly translationService = inject(TranslationService);

    transform(value?: any, format = 'shortDate'): string {
        if (!value) {
            return '';
        }

        const localeCode = this.translationService.getCurrent().code;

        if (format === 'shortDate' && localeCode === 'de') {
            format = 'dd.MM.yyyy';
        }

        if (format === 'mediumCustom') {
            // this custom format returns a localized version of GMT+01 or GMT+02
            // For 'de' locale this would be MEZ or MESZ
            // if the zone timezone can't be mapped it fallsback to 'long'

            const zone = this.getZoneString(value.toString(), localeCode);

            if (zone === '') {
                // time was neither GMT+01 or GMT+02
                // or there is no abbreviation found in the mapping
                // return built in format with time zone

                return formatDate(value, 'long', localeCode);
            }

            // there is no built in format displaying medium date and short time
            // we need to build the string
            const date = formatDate(value, 'fullDate', localeCode);
            const time = formatDate(value, 'shortTime', localeCode);

            return `${date}, ${time} ${zone}`;
        }

        if (format === 'longCustom') {
            // this custom format returns a similar version to the 'long' built-in version
            // but without seconds and with the timezone as CET/CEST
            // if the zone timezone can't be mapped it fallsback to 'long'

            const zone = this.getZoneString(value.toString(), localeCode);

            if (zone === '') {
                return formatDate(value, 'long', localeCode);
            }

            const date = formatDate(value, 'fullDate', localeCode);
            const time = formatDate(value, 'shortTime', localeCode);

            return `${date}, ${time} ${zone}`;
        }

        return formatDate(value, format, this.translationService.getCurrent().code);
    }

    private getZoneString(dateString: string, localeCode: string): string {
        let zone = '';

        if (dateString.includes('GMT+01')) {
            zone = this.getTimeZoneAbbreviations()[localeCode + '_1'];
        } else if (dateString.includes('GMT+02')) {
            zone = this.getTimeZoneAbbreviations()[localeCode + '_2'];
        }

        return zone;
    }

    // Quick and dirty mapping from the few locales currently implemented
    // MEZ = Mitteleuropäische Zeit
    // CET = Central European Time
    // MESZ = Mitteleuropäische Sommerzeit
    // CEST = Central European Summer Time
    private getTimeZoneAbbreviations(): { [key: string]: string } {
        const abbreviatons: { [key: string]: string } = {
            de_1: 'MEZ',
            de_2: 'MESZ',
            en_1: 'CET',
            en_2: 'CEST',
            fr_1: 'HNEC',
            fr_2: 'HEEC',
            it_1: 'CET',
            it_2: 'CEST'
        };

        return abbreviatons;
    }
}
